<template>
  <div class="register">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header bg-primary text-white">
            <h4 class="mb-0">{{ $t('register.title') }}</h4>
          </div>
          <div class="card-body">
            <div v-if="error" class="alert alert-danger">{{ error }}</div>
            <div v-if="success" class="alert alert-success">{{ success }}</div>
            
            <br>
            <!-- Step 1: Collect Info -->
            <div v-if="step === 1">
              <form @submit.prevent="handleSendCode">
                <div class="row">
                  <div class="col-md-6">
                    <h5 class="mb-3">{{ $t('register.personal_info') }}</h5>
                    
                    <div class="mb-3">
                      <label for="firstName" class="form-label">{{ $t('register.first_name') }}</label>
                      <input 
                        type="text" 
                        class="form-control" 
                        id="firstName" 
                        v-model="firstName" 
                        required
                      >
                    </div>
                    
                    <div class="mb-3">
                      <label for="lastName" class="form-label">{{ $t('register.last_name') }}</label>
                      <input 
                        type="text" 
                        class="form-control" 
                        id="lastName" 
                        v-model="lastName" 
                        required
                      >
                    </div>
                    
                    <div class="mb-3">
                      <label for="email" class="form-label">{{ $t('register.email') }}</label>
                      <input 
                        type="email" 
                        class="form-control" 
                        id="email" 
                        v-model="email" 
                        required
                      >
                    </div>
                    
                    <div class="mb-3">
                      <label for="password" class="form-label">{{ $t('register.password') }}</label>
                      <input 
                        type="password" 
                        class="form-control" 
                        id="password" 
                        v-model="password" 
                        required
                        minlength="8"
                      >
                    </div>
                    
                    <div class="mb-3">
                      <label for="confirmPassword" class="form-label">{{ $t('register.confirm_password') }}</label>
                      <input 
                        type="password" 
                        class="form-control" 
                        id="confirmPassword" 
                        v-model="confirmPassword" 
                        required
                      >
                      <div class="form-text text-danger" v-if="passwordMismatch">
                        {{ $t('register.passwords_mismatch') }}
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <h5 class="mb-3">{{ $t('register.company_info') }}</h5>

                    <div class="mb-3">
                      <label for="companyVatId" class="form-label">{{ $t('register.vat_id') }}</label>
                      <div class="input-group">
                        <input 
                          type="text" 
                          class="form-control" 
                          id="companyVatId" 
                          v-model="companyVatId" 
                          required
                          placeholder="e.g. RO123456"
                        >
                        <button 
                          class="btn btn-outline-secondary" 
                          type="button" 
                          @click="lookupVat"
                          :disabled="!companyVatId || vatLoading"
                        >
                          <span v-if="vatLoading" class="spinner-border spinner-border-sm"></span>
                          <span v-else>{{ $t('register.get_details') }}</span>
                        </button>
                      </div>
                      <div class="form-text">
                        {{ $t('register.vat_hint') }}
                      </div>
                    </div>
                    
                    <div class="mb-3">
                      <label for="companyName" class="form-label">{{ $t('register.company_name') }}</label>
                      <input 
                        type="text" 
                        class="form-control" 
                        id="companyName" 
                        v-model="companyName" 
                        required
                      >
                    </div>

                    <div class="mb-3">
                      <label for="regComert" class="form-label">{{ $t('register.reg_comert') }}</label>
                      <input 
                        type="text" 
                        class="form-control" 
                        id="regComert" 
                        v-model="regComert" 
                        placeholder="e.g. J40/1234/2023"
                      >
                    </div>

                    <div class="mb-3">
                      <label for="companyAddress" class="form-label">{{ $t('register.address') }}</label>
                      <textarea 
                        class="form-control" 
                        id="companyAddress" 
                        v-model="companyAddress" 
                        rows="2"
                      ></textarea>
                    </div>

                    <div class="row">
                      <div class="col-6 mb-3">
                        <label for="city" class="form-label">{{ $t('register.city') }}</label>
                        <input 
                          type="text" 
                          class="form-control" 
                          id="city" 
                          v-model="city" 
                        >
                      </div>
                      <div class="col-6 mb-3">
                        <label for="county" class="form-label">{{ $t('register.county') }}</label>
                        <input 
                          type="text" 
                          class="form-control" 
                          id="county" 
                          v-model="county" 
                        >
                      </div>
                    </div>

                    <div class="mb-3">
                      <label for="country" class="form-label">{{ $t('register.country') }}</label>
                      <input 
                        type="text" 
                        class="form-control" 
                        id="country" 
                        v-model="country" 
                      >
                    </div>

                  </div>
                </div>
                
                <div class="d-grid mt-3">
                  <button 
                    type="submit" 
                    class="btn btn-primary" 
                    :disabled="loading || passwordMismatch"
                  >
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    {{ $t('register.verify_btn') }}
                  </button>
                </div>
              </form>
            </div>

            <!-- Step 2: Verification Code -->
            <div v-if="step === 2">
              <div class="text-center mb-4">
                <h5>{{ $t('register.step2_title') }}</h5>
                <p class="text-muted" v-html="$t('register.step2_instruction', { email })"></p>
              </div>

              <form @submit.prevent="handleRegister">
                  <div class="mb-3 col-md-6 mx-auto">
                    <label for="verificationCode" class="form-label">{{ $t('register.code_label') }}</label>
                    <input 
                        type="text" 
                        class="form-control text-center fs-4" 
                        id="verificationCode" 
                        v-model="verificationCode" 
                        placeholder=""
                        required
                        maxlength="6"
                    >
                  </div>
                  
                  <div class="d-grid gap-2 col-md-6 mx-auto">
                    <button 
                        type="submit" 
                        class="btn btn-success" 
                        :disabled="loading"
                    >
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                        {{ $t('register.complete_btn') }}
                    </button>
                    <button 
                        type="button" 
                        class="btn btn-outline-secondary" 
                        @click="step = 1"
                        :disabled="loading"
                    >
                        {{ $t('register.back_btn') }}
                    </button>
                  </div>
              </form>
            </div>
            
            <div class="mt-3 text-center">
              <p v-html="$t('register.login_link')"></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import axios from '../axios' // Assuming axios instance is configured in/near main

export default {
  name: 'Register',
  data() {
    return {
      step: 1,
      verificationCode: '',
      
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      companyName: '',
      companyVatId: '',
      companyAddress: '',
      regComert: '',
      city: '',
      county: '',
      country: '',
      
      error: null,
      success: null,
      loading: false,
      vatLoading: false
    }
  },
  computed: {
    passwordMismatch() {
      return this.password && this.confirmPassword && this.password !== this.confirmPassword
    }
  },
  methods: {
    ...mapActions('auth', ['register']),

    async lookupVat() {
      if (!this.companyVatId) return;
      this.vatLoading = true;
      this.error = null;

      try {
        const response = await axios.post('/api/util/vat-lookup', {
          vat_id: this.companyVatId
        });

        if (response.data.success && response.data.valid) {
          const data = response.data;
          this.companyName = data.name || this.companyName;
          this.companyAddress = data.address || this.companyAddress;
          
          if (data.countryCode) {
             this.country = data.countryCode; 
          }
          
        } else if (response.data.success && !response.data.valid) {
             this.error = "VAT ID is invalid or not found in VIES.";
        }
      } catch (err) {
        console.error("VIES Lookup Error", err);
        this.error = "Could not retrieve company details. Please enter manually.";
      } finally {
        this.vatLoading = false;
      }
    },
    
    async handleSendCode() {
        if (this.passwordMismatch) {
            this.error = 'Passwords do not match';
            return;
        }
        
        this.loading = true;
        this.error = null;
        
        try {
            // Call API to send code
            const response = await axios.post('/api/auth/send-code', {
                email: this.email
            });
            
            if (response.data.success) {
                this.step = 2;
                this.success = "Verification code sent! Please check your email.";
            } else {
                 this.error = response.data.error || "Failed to send code.";
            }
        } catch (err) {
            this.error = err.response ? err.response.data.error : "Failed to send verification email.";
        } finally {
            this.loading = false;
        }
    },

    async handleRegister() {
      if (!this.verificationCode) {
           this.error = "Please enter the verification code.";
           return;
      }
      
      this.loading = true
      this.error = null
      this.success = null
      
      try {
        const result = await this.register({
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          password: this.password,
          companyName: this.companyName,
          companyVatId: this.companyVatId,
          companyAddress: this.companyAddress,
          regComert: this.regComert,
          city: this.city,
          county: this.county,
          country: this.country,
          code: this.verificationCode
        })
        
        if (result.success) {
          this.success = 'Registration successful! You can now login.'
          // Reset form (optional, since we redirect)
          this.step = 1;
          this.verificationCode = '';
          
          // Redirect to login after a delay
            this.$router.push({ path: '/login', query: { email: this.email } })

        } else {
          this.error = result.error
        }
      } catch (error) {
        this.error = 'An unexpected error occurred. Please try again.'
        console.error(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>